import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { categories, videos, siteInfo } from "@/mocks/videoData";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/pages/home/components/VideoCard";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "remen-tuijian");
  const [selectedSource, setSelectedSource] = useState("all");

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];

  const filteredVideos = (() => {
    let result = videos;
    if (activeCategory === "remen-tuijian") {
      result = [...videos].sort((a, b) => parseFloat(b.views.replace("万", "")) - parseFloat(a.views.replace("万", "")));
    } else if (activeCategory === "zuixin-shangxian") {
      result = [...videos].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    } else {
      result = videos.filter((v) => v.category === activeCategory);
    }
    if (selectedSource !== "all") {
      result = result.filter((v) => v.source === selectedSource);
    }
    return result;
  })();

  const sources = ["all", ...new Set(videos.map((v) => v.source))];

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb items={[{ name: currentCategory.name }]} />
          </div>
        </div>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-600 mb-6">
              共找到 {filteredVideos.length} 个视频资源
            </p>

            <div className="flex flex-col md:flex-row gap-6">
              {/* 侧边栏 */}
              <aside className="w-full md:w-[220px] flex-shrink-0">
                <div className="bg-background-50 border border-background-200 rounded-lg p-4 sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-3">资源分类</h3>
                  <nav className="flex flex-col gap-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        onClick={() => {
                          setActiveCategory(cat.slug);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                          activeCategory === cat.slug
                            ? "bg-primary-500 text-white"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </nav>

                  <h3 className="text-sm font-semibold text-foreground-950 mt-6 mb-3">来源平台</h3>
                  <div className="flex flex-col gap-1">
                    {sources.map((src) => (
                      <button
                        key={src}
                        onClick={() => setSelectedSource(src)}
                        className={`text-left px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                          selectedSource === src
                            ? "bg-secondary-100 text-secondary-900 font-medium"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        {src === "all" ? "全部平台" : src}
                      </button>
                    ))}
                  </div>
                </div>
              </aside>

              {/* 视频网格 */}
              <div className="flex-1">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
                  {filteredVideos.map((video) => (
                    <VideoCard key={video.id} {...video} />
                  ))}
                </div>

                {filteredVideos.length === 0 && (
                  <div className="text-center py-20 text-foreground-500">
                    <i className="ri-video-line text-5xl mb-4 block"></i>
                    <p className="text-lg">暂无该分类的视频资源</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "首页",
                    item: siteInfo.url,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: currentCategory.name,
                    item: `${siteInfo.url}/category/${currentCategory.slug}`,
                  },
                ],
              },
              {
                "@type": "WebPage",
                url: `${siteInfo.url}/category/${currentCategory.slug}`,
                name: `${currentCategory.name} - ${siteInfo.name}`,
                isPartOf: { "@id": `${siteInfo.url}/#website` },
              },
            ],
          }),
        }}
      />
    </>
  );
}